import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * BottomSheet — 지도 페이지 드래그 시트.
 *
 * 정책 무관 (구조 컴포넌트).
 * RN 친화 점:
 *   - window/document 의존은 onSnap callback 안에만 한정.
 *   - 위치 제어는 props로 (state hoisting 가능).
 */

export type BottomSheetSnap = 'collapsed' | 'mid' | 'expanded';

export interface BottomSheetProps {
  snap?: BottomSheetSnap;
  onSnapChange?: (snap: BottomSheetSnap) => void;
  /** 각 snap 포인트의 시트 높이 (viewport 비율 0~1). 기본 (0.18, 0.5, 0.92). */
  snapPoints?: { collapsed: number; mid: number; expanded: number };
  children: React.ReactNode;
  className?: string;
  /** 시트 위 헤더 (가로 스크롤 카드 등) */
  header?: React.ReactNode;
}

const DEFAULT_POINTS = { collapsed: 0.18, mid: 0.5, expanded: 0.92 };

export const BottomSheet: React.FC<BottomSheetProps> = ({
  snap = 'mid',
  onSnapChange,
  snapPoints = DEFAULT_POINTS,
  children,
  className = '',
  header,
}) => {
  const [currentSnap, setCurrentSnap] = useState<BottomSheetSnap>(snap);
  const startY = useRef<number | null>(null);
  const startSnap = useRef<BottomSheetSnap>(snap);

  useEffect(() => {
    setCurrentSnap(snap);
  }, [snap]);

  const heightPct = (snapPoints[currentSnap] * 100).toFixed(1);

  const goTo = useCallback((next: BottomSheetSnap) => {
    setCurrentSnap(next);
    onSnapChange?.(next);
  }, [onSnapChange]);

  const onTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    startSnap.current = currentSnap;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const dy = e.changedTouches[0].clientY - startY.current;
    const THRESHOLD = 60;
    const order: BottomSheetSnap[] = ['collapsed', 'mid', 'expanded'];
    const idx = order.indexOf(startSnap.current);
    if (dy < -THRESHOLD && idx < 2) goTo(order[idx + 1]);
    else if (dy > THRESHOLD && idx > 0) goTo(order[idx - 1]);
    startY.current = null;
  };

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-10 rounded-t-ca-4xl border-t border-ca-line bg-frame shadow-ca-sheet transition-[height] duration-200 ease-out ${className}`}
      style={{ height: `${heightPct}%` }}
    >
      <div
        className="touch-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        role="slider"
        aria-label="시트 드래그"
        aria-valuenow={Number(heightPct)}
      >
        <div className="mx-auto mt-2 mb-1 h-1 w-9 rounded-full bg-mute3" />
      </div>
      {header && <div className="border-b border-ca-line-soft px-3.5 py-2">{header}</div>}
      <div className="h-full overflow-y-auto pb-6">{children}</div>
    </div>
  );
};

export default BottomSheet;
